import websocketService from '../api/websocket';

const WebSocketPlugin = {
  install(Vue, options = {}) {
    // Default configuration
    const config = {
      url: process.env.VUE_APP_WS_URL || 'wss://api.gurmanlogistics.com/ws',
      autoConnect: true,
      reconnectAttempts: 5,
      reconnectInterval: 3000,
      ...options
    };

    // Set configuration
    websocketService.maxReconnectAttempts = config.reconnectAttempts;
    websocketService.reconnectInterval = config.reconnectInterval;

    // Add to Vue prototype for global access
    Vue.prototype.$ws = websocketService;
    Vue.prototype.$websocket = websocketService; // Alternative name

    // Global reactive connection status
    Vue.prototype.$wsStatus = Vue.observable({
      connected: false,
      connecting: false,
      error: null,
      channels: []
    });

    // Listen to connection events and update status
    websocketService.on('connected', () => {
      Vue.prototype.$wsStatus.connected = true;
      Vue.prototype.$wsStatus.connecting = false;
      Vue.prototype.$wsStatus.error = null;
    });

    websocketService.on('disconnected', () => {
      Vue.prototype.$wsStatus.connected = false;
      Vue.prototype.$wsStatus.connecting = false;
    });

    websocketService.on('error', (error) => {
      Vue.prototype.$wsStatus.error = error;
      Vue.prototype.$wsStatus.connecting = false;
    });

    // Auto-connect if enabled
    if (config.autoConnect) {
      websocketService.connect(config.url);
    }

    // Global mixin for easy component integration
    Vue.mixin({
      data() {
        return {
          wsListeners: []
        };
      },
      methods: {
        // Convenient methods for components
        $wsOn(event, callback) {
          websocketService.on(event, callback);
          // Track listeners for cleanup
          this.wsListeners.push({ event, callback });
        },
        
        $wsOff(event, callback) {
          websocketService.off(event, callback);
          // Remove from tracked listeners
          this.wsListeners = this.wsListeners.filter(
            listener => !(listener.event === event && listener.callback === callback)
          );
        },

        $wsOnChannel(channel, eventType, callback) {
          websocketService.onChannel(channel, eventType, callback);
          // Track listeners for cleanup
          this.wsListeners.push({ 
            event: `${channel}:${eventType}`, 
            callback,
            channel,
            eventType
          });
        },

        $wsOffChannel(channel, eventType, callback) {
          websocketService.offChannel(channel, eventType, callback);
          // Remove from tracked listeners
          this.wsListeners = this.wsListeners.filter(
            listener => !(listener.channel === channel && listener.eventType === eventType && listener.callback === callback)
          );
        },

        $wsSubscribe(channel) {
          websocketService.subscribe(channel);
          // Update global status
          if (!Vue.prototype.$wsStatus.channels.includes(channel)) {
            Vue.prototype.$wsStatus.channels.push(channel);
          }
        },

        $wsUnsubscribe(channel) {
          websocketService.unsubscribe(channel);
          // Update global status
          const index = Vue.prototype.$wsStatus.channels.indexOf(channel);
          if (index > -1) {
            Vue.prototype.$wsStatus.channels.splice(index, 1);
          }
        },

        $wsSend(data) {
          websocketService.send(data);
        }
      },
      
      // Automatically clean up listeners when component is destroyed
      beforeDestroy() {
        this.wsListeners.forEach(({ event, callback, channel, eventType }) => {
          if (channel && eventType) {
            websocketService.offChannel(channel, eventType, callback);
          } else {
            websocketService.off(event, callback);
          }
        });
        this.wsListeners = [];
      }
    });

    // Add global event bus for cross-component communication
    Vue.prototype.$wsEventBus = new Vue();

    // Forward WebSocket events to the event bus for reactive components
    const forwardedEvents = [
      'connected', 'disconnected', 'error', 'message',
      'loads:new_load', 'loads:load_updated', 'loads:load_deleted',
      'users:user_created', 'users:user_updated', 'users:user_deleted',
      'notifications:new_notification', 'system:maintenance'
    ];

    forwardedEvents.forEach(event => {
      websocketService.on(event, (data) => {
        Vue.prototype.$wsEventBus.$emit(event, data);
      });
    });
  }
};

export default WebSocketPlugin;
